import { useState, useCallback } from "react";

/* ─── TOAST HOOK ─────────────────────────────────────────────────────────── */
export function useToast() {
  const [toasts, setToasts] = useState([]);

  const toast = useCallback((message, type = "success", duration = 2800) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
  }, []);

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return { toasts, toast, dismiss };
}

/* ─── TOAST CONTAINER ────────────────────────────────────────────────────── */
const ICONS = { success: "✓", error: "✕", info: "ℹ", warning: "⚠" };
const COLORS = {
  success: { bg: "rgba(21,128,61,0.95)",   border: "rgba(74,222,128,0.35)",  text: "#fff" },
  error:   { bg: "rgba(185,28,28,0.95)",   border: "rgba(248,113,113,0.35)", text: "#fff" },
  info:    { bg: "rgba(30,64,175,0.95)",   border: "rgba(147,197,253,0.35)", text: "#fff" },
  warning: { bg: "rgba(146,64,14,0.95)",   border: "rgba(251,191,36,0.35)",  text: "#fff" },
};

export function ToastContainer({ toasts, dismiss }) {
  if (!toasts || !toasts.length) return null;
  return (
    <div style={{
      position: "fixed", bottom: "1.5rem", right: "1.5rem",
      display: "flex", flexDirection: "column", gap: "0.5rem",
      zIndex: 9999, pointerEvents: "none",
    }}>
      {toasts.map(t => {
        const c = COLORS[t.type] || COLORS.success;
        return (
          <div key={t.id} onClick={() => dismiss(t.id)} style={{
            pointerEvents: "all", cursor: "pointer",
            display: "flex", alignItems: "center", gap: "0.6rem",
            background: c.bg, border: `1px solid ${c.border}`,
            color: c.text, borderRadius: "10px",
            padding: "0.65rem 1rem",
            fontFamily: "var(--font-mono, 'IBM Plex Mono', monospace)",
            fontSize: "0.75rem", fontWeight: 500, letterSpacing: "0.02em",
            backdropFilter: "blur(8px)",
            boxShadow: "0 4px 24px rgba(0,0,0,0.35)",
            animation: "toastIn 0.22s cubic-bezier(0.34,1.56,0.64,1)",
            maxWidth: "320px", lineHeight: 1.4,
          }}>
            <span style={{ fontSize: "0.85rem", flexShrink: 0 }}>{ICONS[t.type] || ICONS.success}</span>
            <span>{t.message}</span>
          </div>
        );
      })}
      <style>{`@keyframes toastIn{from{opacity:0;transform:translateX(20px) scale(0.95)}to{opacity:1;transform:translateX(0) scale(1)}}`}</style>
    </div>
  );
}

/* ─── SKELETON COMPONENTS ────────────────────────────────────────────────── */
const shimmer = `
  @keyframes shimmer{0%{background-position:-400px 0}100%{background-position:400px 0}}
  .skel{border-radius:6px;background:linear-gradient(90deg,var(--surface2,#1a1a24) 25%,var(--border,#2a2a38) 50%,var(--surface2,#1a1a24) 75%);background-size:800px 100%;animation:shimmer 1.4s infinite linear;}
`;

function Skel({ w = "100%", h = "14px", style = {} }) {
  return <div className="skel" style={{ width: w, height: h, borderRadius: 6, ...style }} />;
}

/* Bill list skeleton — matches the bt-bill-item shape */
export function BillListSkeleton({ count = 8 }) {
  return (
    <>
      <style>{shimmer}</style>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={{ padding: "0.85rem 1.1rem", borderBottom: "1px solid var(--border)", display: "flex", flexDirection: "column", gap: "0.45rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem" }}>
            <Skel w="72px" h="11px" />
            <Skel w="56px" h="11px" />
          </div>
          <Skel w="90%" h="13px" />
          <Skel w="65%" h="11px" />
        </div>
      ))}
    </>
  );
}

/* Widget skeleton — matches dashboard widget card shape */
export function WidgetSkeleton({ rows = 4 }) {
  return (
    <>
      <style>{shimmer}</style>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem", padding: "0.5rem 0" }}>
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.5rem 0", borderBottom: "1px solid var(--border)" }}>
            <Skel w="40px" h="40px" style={{ borderRadius: 8, flexShrink: 0 }} />
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "0.35rem" }}>
              <Skel w="55%" h="12px" />
              <Skel w="80%" h="11px" />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

/* Meeting list skeleton — matches calendar/meeting list shape */
export function MeetingListSkeleton({ count = 5 }) {
  return (
    <>
      <style>{shimmer}</style>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={{ padding: "0.75rem 1rem", borderBottom: "1px solid var(--border)", display: "flex", flexDirection: "column", gap: "0.4rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Skel w="48px" h="10px" />
            <Skel w="52px" h="10px" />
          </div>
          <Skel w="85%" h="13px" />
          <Skel w="50%" h="10px" />
        </div>
      ))}
    </>
  );
}

/* Table row skeleton — matches TrackedLegislation / ClientPortal table rows */
export function TableRowSkeleton({ cols = 5, rows = 6 }) {
  return (
    <>
      <style>{shimmer}</style>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i}>
          {Array.from({ length: cols }).map((_, j) => (
            <td key={j} style={{ padding: "0.65rem 0.85rem", borderBottom: "1px solid var(--border)" }}>
              <Skel w={j === 0 ? "72px" : j === cols - 1 ? "48px" : "80%"} h="12px" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/* Directory card skeleton */
export function DirectoryCardSkeleton({ count = 6 }) {
  return (
    <>
      <style>{shimmer}</style>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={{ padding: "0.75rem 1rem", borderBottom: "1px solid var(--border)", display: "flex", flexDirection: "column", gap: "0.4rem" }}>
          <Skel w="60%" h="13px" />
          <Skel w="40%" h="11px" />
          <Skel w="75%" h="10px" />
        </div>
      ))}
    </>
  );
}

/* Generic line skeleton for simple lists */
export function LineSkeleton({ count = 5 }) {
  return (
    <>
      <style>{shimmer}</style>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem", padding: "0.75rem 0" }}>
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
            <Skel w={`${60 + (i % 3) * 12}%`} h="13px" />
            <Skel w={`${40 + (i % 4) * 8}%`} h="10px" />
          </div>
        ))}
      </div>
    </>
  );
}

/* ─── CONFIRM DIALOG ─────────────────────────────────────────────────────── */
export function ConfirmDialog({ message, subtext, confirmLabel = "Delete", confirmStyle = "danger", onConfirm, onCancel }) {
  return (
    <div style={{
      position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",backdropFilter:"blur(4px)",
      zIndex:9998,display:"flex",alignItems:"center",justifyContent:"center",padding:"1.5rem"
    }} onClick={onCancel}>
      <div style={{
        background:"var(--surface)",border:"1px solid var(--border)",borderRadius:"14px",
        width:"100%",maxWidth:"400px",overflow:"hidden",
        fontFamily:"var(--font-mono,'IBM Plex Mono',monospace)"
      }} onClick={e => e.stopPropagation()}>
        <div style={{padding:"1.4rem 1.5rem",borderBottom:"1px solid var(--border)"}}>
          <div style={{fontSize:"0.88rem",fontWeight:700,color:"var(--text)",marginBottom:subtext?"0.4rem":0}}>{message}</div>
          {subtext && <div style={{fontSize:"0.72rem",color:"var(--muted)",lineHeight:1.5}}>{subtext}</div>}
        </div>
        <div style={{padding:"1rem 1.5rem",display:"flex",justifyContent:"flex-end",gap:"0.5rem"}}>
          <button onClick={onCancel} style={{background:"transparent",border:"1px solid var(--border)",borderRadius:"8px",padding:"0.45rem 1rem",fontSize:"0.68rem",color:"var(--muted)",cursor:"pointer",transition:"all 0.15s"}}>Cancel</button>
          <button onClick={onConfirm} style={{background:confirmStyle==="danger"?"rgba(192,57,43,0.9)":"var(--gold)",border:"none",borderRadius:"8px",padding:"0.45rem 1rem",fontSize:"0.68rem",fontWeight:700,color:confirmStyle==="danger"?"#fff":"#09090e",cursor:"pointer",transition:"opacity 0.15s"}}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

/* ─── BILL STATUS BADGE ─────────────────────────────────────────────────── */
const STATUS_CONFIG = {
  "PA Signed":   { color:"#3ba360", bg:"rgba(59,163,96,0.1)",  border:"rgba(59,163,96,0.25)",  icon:"✓", label:"PA Signed"     },
  "Chaptered":   { color:"#3ba360", bg:"rgba(59,163,96,0.1)",  border:"rgba(59,163,96,0.25)",  icon:"✓", label:"PA Signed"     },
  "Enrolled":    { color:"#3ba360", bg:"rgba(59,163,96,0.1)",  border:"rgba(59,163,96,0.25)",  icon:"✓", label:"Enrolled"      },
  "Passed":      { color:"#3ba360", bg:"rgba(59,163,96,0.1)",  border:"rgba(59,163,96,0.25)",  icon:"✓", label:"Passed"        },
  "Engrossed":   { color:"#c9a84c", bg:"rgba(201,168,76,0.1)", border:"rgba(201,168,76,0.25)", icon:"→", label:"Passed Chamber" },
  "In Progress": { color:"#c9a84c", bg:"rgba(201,168,76,0.1)", border:"rgba(201,168,76,0.25)", icon:"◆", label:"In Progress"   },
  "In Committee":{ color:"#c9a84c", bg:"rgba(201,168,76,0.1)", border:"rgba(201,168,76,0.25)", icon:"◆", label:"In Committee"  },
  "Introduced":  { color:"#8b9ab0", bg:"rgba(139,154,176,0.1)",border:"rgba(139,154,176,0.25)",icon:"●", label:"Introduced"    },
  "Failed":      { color:"#c0392b", bg:"rgba(192,57,43,0.1)",  border:"rgba(192,57,43,0.25)",  icon:"✕", label:"Failed"        },
  "Vetoed":      { color:"#c0392b", bg:"rgba(192,57,43,0.1)",  border:"rgba(192,57,43,0.25)",  icon:"✕", label:"Vetoed"        },
  "Dead":        { color:"#c0392b", bg:"rgba(192,57,43,0.1)",  border:"rgba(192,57,43,0.25)",  icon:"✕", label:"Dead"          },
};

function resolveStatus(status) {
  if (!status) return STATUS_CONFIG["Introduced"];
  const s = status.trim();
  if (STATUS_CONFIG[s]) return STATUS_CONFIG[s];
  const sl = s.toLowerCase();
  if (sl.includes("pa signed") || sl.includes("chapter") || sl.includes("signed")) return STATUS_CONFIG["PA Signed"];
  if (sl.includes("enrolled")) return STATUS_CONFIG["Enrolled"];
  if (sl.includes("passed")) return STATUS_CONFIG["Passed"];
  if (sl.includes("engrossed")) return STATUS_CONFIG["Engrossed"];
  if (sl.includes("committee") || sl.includes("refer")) return STATUS_CONFIG["In Committee"];
  if (sl.includes("progress")) return STATUS_CONFIG["In Progress"];
  if (sl.includes("failed") || sl.includes("fail")) return STATUS_CONFIG["Failed"];
  if (sl.includes("veto")) return STATUS_CONFIG["Vetoed"];
  if (sl.includes("dead") || sl.includes("withdrawn")) return STATUS_CONFIG["Dead"];
  return STATUS_CONFIG["Introduced"];
}

export function BillStatusBadge({ status, lastAction, chamber, lastActionDate }) {
  const cfg = resolveStatus(status);
  return (
    <div style={{margin:"0.6rem 0 0.25rem",display:"flex",flexDirection:"column",gap:"0.45rem"}}>
      <div style={{display:"flex",alignItems:"center",gap:"0.6rem",flexWrap:"wrap"}}>
        <span style={{display:"inline-flex",alignItems:"center",gap:"0.35rem",background:cfg.bg,border:`1px solid ${cfg.border}`,borderRadius:"20px",padding:"0.25rem 0.75rem",fontFamily:"var(--font-mono,monospace)",fontSize:"0.68rem",fontWeight:600,color:cfg.color,letterSpacing:"0.04em",whiteSpace:"nowrap"}}>
          <span style={{fontSize:"0.7rem"}}>{cfg.icon}</span>
          {cfg.label}
        </span>
        {chamber && (
          <span style={{fontFamily:"var(--font-mono,monospace)",fontSize:"0.62rem",color:"var(--muted,#888)",background:"var(--surface2,#1a1a24)",border:"1px solid var(--border,#2a2a38)",borderRadius:"4px",padding:"0.18rem 0.5rem"}}>
            {chamber}
          </span>
        )}
        {lastActionDate && (
          <span style={{fontFamily:"var(--font-mono,monospace)",fontSize:"0.6rem",color:"var(--muted,#888)",opacity:0.7}}>
            {new Date(lastActionDate).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
          </span>
        )}
      </div>
      {lastAction && (
        <div style={{fontFamily:"var(--font-mono,monospace)",fontSize:"0.68rem",color:"var(--muted,#888)",lineHeight:1.5,paddingLeft:"0.1rem",opacity:0.85}}>
          {lastAction}
        </div>
      )}
    </div>
  );
}

